// src/features/nav/App.jsx
import { lazy, Suspense, useEffect } from 'react';
import PWAPrompt from 'react-ios-pwa-prompt';
import { useDispatch, useSelector } from 'react-redux';
import { isLoaded } from 'react-redux-firebase';
import { Redirect, Route, Switch } from 'react-router-dom';
import { Container } from 'semantic-ui-react';
import { trackUserAppVersion } from '../../app/common/util/userVersionTracker';
import ErrorBoundary from '../../app/layout/ErrorBoundary';
import LoadingComponent from '../../app/layout/LoadingComponent';
import { completeGoogleRedirectSignIn } from '../../features/auth/authActions';
import { UserIsAuthenticated } from '../../features/auth/authWrapper';
import RequireAuthGate from '../../features/auth/RequireAuthGate';
import Guides from '../../features/wiki/pages/Guides';
import Handbooks from '../../features/wiki/pages/Handbooks';
import Resources from '../../features/wiki/pages/Resources';
import WikiPage from '../../features/wiki/pages/WikiPage';
import NotFound from './NotFound';

// Lazy bundles
const AsyncHomePage = lazy(() => import('../../features/home/HomePage'));
const AsyncFinishEmailSignIn = lazy(
  () => import('../../features/auth/Login/FinishEmailSignIn'),
);
const AsyncDashboard = lazy(() => import('../../features/dashboard/Dashboard'));
const AsyncSessionDashboard = lazy(
  () => import('../../features/session/SessionDashboard/SessionDashboard'),
);
const AsyncPlanDashboard = lazy(
  () => import('../../features/plan/PlanDashboard/PlanDashboard'),
);
const AsyncDocumentDashboard = lazy(
  () => import('../../features/document/DocumentDashboard/DocumentDashboard'),
);
const AsyncSupervisionDashboard = lazy(
  () =>
    import('../../features/supervision/SupervisionDashboard/SupervisionDashboard'),
);
const AsyncReviewDashboard = lazy(
  () => import('../../features/review/ReviewDashboard/ReviewDashboard'),
);
const AsyncEventDashboard = lazy(
  () => import('../../features/event/EventDashboard/EventDashboard'),
);
const AsyncNondirectDashboard = lazy(
  () =>
    import('../../features/nondirect/NondirectDashboard/NondirectDashboard'),
);

const AsyncSessionDetailedPage = lazy(
  () => import('../../features/session/SessionDetailed/SessionDetailedPage'),
);
const AsyncPlanDetailedPage = lazy(
  () => import('../../features/plan/PlanDetailed/PlanDetailedPage'),
);
const AsyncDocumentDetailedPage = lazy(
  () => import('../../features/document/DocumentDetailed/DocumentDetailedPage'),
);
const AsyncSupervisionDetailedPage = lazy(
  () =>
    import('../../features/supervision/SupervisionDetailed/SupervisionDetailedPage'),
);
const AsyncReviewDetailedPage = lazy(
  () => import('../../features/review/ReviewDetailed/ReviewDetailedPage'),
);
const AsyncEventDetailedPage = lazy(
  () => import('../../features/event/EventDetailed/EventDetailedPage'),
);
const AsyncNondirectDetailedPage = lazy(
  () =>
    import('../../features/nondirect/NondirectDetailed/NondirectDetailedPage'),
);

const AsyncSessionForm = lazy(
  () => import('../../features/session/SessionForm/SessionForm'),
);
const AsyncPlanForm = lazy(
  () => import('../../features/plan/PlanForm/PlanForm'),
);
const AsyncDocumentForm = lazy(
  () => import('../../features/document/DocumentForm/DocumentForm'),
);
const AsyncSupervisionForm = lazy(
  () => import('../../features/supervision/SupervisionForm/SupervisionForm'),
);
const AsyncReviewForm = lazy(
  () => import('../../features/review/ReviewForm/ReviewForm'),
);
const AsyncEventForm = lazy(
  () => import('../../features/event/EventForm/EventForm'),
);
const AsyncNondirectForm = lazy(
  () => import('../../features/nondirect/NondirectForm/NondirectForm'),
);

const AsyncPeopleDashboard = lazy(
  () => import('../../features/user/PeopleDashboard/PeopleDashboard'),
);
const AsyncNetworkDashboard = lazy(
  () => import('../../features/user/NetworkDashboard/NetworkDashboard'),
);
const AsyncUserDetailedPage = lazy(
  () => import('../../features/user/UserDetailed/UserDetailedPage'),
);
const AsyncManageUserForm = lazy(
  () => import('../../features/user/ManageUsers/ManageUserForm'),
);
const AsyncSettingsDashboard = lazy(
  () => import('../../features/user/Settings/SettingsDashboard'),
);

const AsyncNavBar = lazy(() => import('../../features/nav/NavBar/NavBar'));
const AsyncUserTutorial = lazy(
  () => import('../../features/tutorial/UserTutorial'),
);
const AsyncNavBarMenu = lazy(
  () => import('../../features/nav/NavBar/NavBarMenu'),
);
const AsyncModalManager = lazy(
  () => import('../../features/modals/ModalManager'),
);
const AsyncActivityLog = lazy(
  () => import('../../features/ActivityLog/ActivityLog'),
);
const AsyncReports = lazy(
  () => import('../../features/report/ReportDashboard'),
);
const AsyncGroupChat = lazy(() => import('../../features/chat/GroupChat'));
const AsyncGustoCallback = lazy(
  () => import('../../features/gusto/GustoCallback'),
);
const AsyncErrorLogPage = lazy(
  () => import('../../features/admin/ErrorLog/ErrorLogPage'),
);
const AsyncTrainingVideos = lazy(
  () => import('../../features/training/TrainingVideos'),
);
const AsyncGroupTrainingDetail = lazy(
  () => import('../../features/training/GroupTrainingDetailPage'),
);

/** ---------- Inline Role Gate (no new files) ---------- */
const RoleGate = ({ allow = [], children }) => {
  const profile = useSelector((s) => s.firebase.profile);
  if (!isLoaded(profile)) return <LoadingComponent />;

  const role = String(profile?.role || '').toLowerCase();
  const ok = allow.map((r) => String(r).toLowerCase()).includes(role);

  return ok ? children : <Redirect to="/home" />;
};

const AdminOnlyErrorLog = () => (
  <RoleGate allow={['Administrator', 'Program Coordinator']}>
    <AsyncErrorLogPage />
  </RoleGate>
);

// wrappers for protected pages
const ManagerOnlySupervisions = () => (
  <RoleGate
    allow={[
      'Administrator',
      'Program Coordinator',
      'Program Manager',
      'Auditor',
    ]}
  >
    <AsyncSupervisionDashboard />
  </RoleGate>
);

const ManagerOnlyReports = () => (
  <RoleGate allow={['Administrator', 'Program Coordinator', 'Program Manager']}>
    <AsyncReports />
  </RoleGate>
);
const NonClientTraining = (props) => (
  <RoleGate
    allow={[
      'Administrator',
      'Program Coordinator',
      'Program Manager',
      'Behavioral Health Professional',
      'Auditor',
    ]}
  >
    <AsyncTrainingVideos {...props} />
  </RoleGate>
);

const NonClientGroupTraining = (props) => (
  <RoleGate
    allow={[
      'Administrator',
      'Program Coordinator',
      'Program Manager',
      'Behavioral Health Professional',
      'Auditor',
    ]}
  >
    <AsyncGroupTrainingDetail {...props} />
  </RoleGate>
);
/** ------------------------------------------------------ */

const App = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.firebase.auth);

  // Complete a Google sign-in that fell back to redirect (popup blocked).
  useEffect(() => {
    dispatch(completeGoogleRedirectSignIn());
  }, [dispatch]);

  // Track the version of the portal the signed-in user is running.
  useEffect(() => {
    if (isLoaded(auth) && !auth.isEmpty && auth.uid) {
      trackUserAppVersion(auth.uid);
    }
  }, [auth]);

  return (
    <ErrorBoundary>
      <div>
        <PWAPrompt
          timesToShow={3}
          permanentlyHideOnDismiss={false}
          appIconPath="/icons/icon-192x192.png"
          copyTitle="Add to Home Screen"
          copyBody="Install React Portal as an app for quick access and offline use."
          copyShareButtonLabel="1) Press the 'Share' button"
          copyAddHomeButtonLabel="2) Press 'Add to Home Screen'"
        />

        <Suspense fallback={<LoadingComponent />}>
          <AsyncModalManager />
        </Suspense>

        <Suspense fallback={<LoadingComponent />}>
          <Route exact path="/" component={AsyncHomePage} />
        </Suspense>

        <Route
          path="/(.+)"
          render={() => (
            <div>
              <Suspense fallback={<LoadingComponent />}>
                <AsyncNavBar />
              </Suspense>

              <Suspense fallback={<LoadingComponent />}>
                <AsyncNavBarMenu />
              </Suspense>

              <Suspense fallback={null}>
                <AsyncUserTutorial />
              </Suspense>

              <Container className="main">
                <RequireAuthGate />

                <Suspense fallback={<LoadingComponent />}>
                  <Switch>
                    <Route
                      path="/finishSignIn"
                      component={AsyncFinishEmailSignIn}
                    />
                    <Route
                      path="/home"
                      component={UserIsAuthenticated(AsyncDashboard)}
                    />
                    <Route
                      path="/sessions"
                      component={UserIsAuthenticated(AsyncSessionDashboard)}
                    />
                    <Route
                      path="/plans"
                      component={UserIsAuthenticated(AsyncPlanDashboard)}
                    />
                    <Route
                      path="/documents"
                      component={UserIsAuthenticated(AsyncDocumentDashboard)}
                    />

                    {/* 🔒 Managers only (BHP/Client => /home) */}
                    <Route
                      path="/supervisions"
                      component={UserIsAuthenticated(ManagerOnlySupervisions)}
                    />
                    <Route
                      path="/reports"
                      component={UserIsAuthenticated(ManagerOnlyReports)}
                    />

                    <Route
                      path="/reviews"
                      component={UserIsAuthenticated(AsyncReviewDashboard)}
                    />
                    <Route
                      path="/events"
                      component={UserIsAuthenticated(AsyncEventDashboard)}
                    />
                    <Route
                      path="/nondirects"
                      component={UserIsAuthenticated(AsyncNondirectDashboard)}
                    />
                    <Route
                      path="/activity"
                      component={UserIsAuthenticated(AsyncActivityLog)}
                    />

                    <Route
                      path="/session/:id"
                      component={UserIsAuthenticated(AsyncSessionDetailedPage)}
                    />
                    <Route
                      path="/plan/:id"
                      component={UserIsAuthenticated(AsyncPlanDetailedPage)}
                    />
                    <Route
                      path="/document/:id"
                      component={UserIsAuthenticated(AsyncDocumentDetailedPage)}
                    />
                    <Route
                      path="/supervision/:id"
                      component={UserIsAuthenticated(
                        AsyncSupervisionDetailedPage,
                      )}
                    />
                    <Route
                      path="/review/:id"
                      component={UserIsAuthenticated(AsyncReviewDetailedPage)}
                    />
                    <Route
                      path="/event/:id"
                      component={UserIsAuthenticated(AsyncEventDetailedPage)}
                    />
                    <Route
                      path="/nondirect/:id"
                      component={UserIsAuthenticated(
                        AsyncNondirectDetailedPage,
                      )}
                    />

                    <Route
                      path="/myNetwork"
                      component={UserIsAuthenticated(AsyncPeopleDashboard)}
                    />
                    <Route
                      path="/network"
                      component={UserIsAuthenticated(AsyncNetworkDashboard)}
                    />
                    <Route
                      path="/profile/:id"
                      component={UserIsAuthenticated(AsyncUserDetailedPage)}
                    />
                    <Route
                      path="/settings"
                      component={UserIsAuthenticated(AsyncSettingsDashboard)}
                    />

                    <Route
                      path={['/createSession', '/manageSession/:id']}
                      component={UserIsAuthenticated(AsyncSessionForm)}
                    />
                    <Route
                      path={['/createPlan', '/managePlan/:id']}
                      component={UserIsAuthenticated(AsyncPlanForm)}
                    />
                    <Route
                      path={['/createDocument', '/manageDocument/:id']}
                      component={UserIsAuthenticated(AsyncDocumentForm)}
                    />
                    <Route
                      path={['/createSupervision', '/manageSupervision/:id']}
                      component={UserIsAuthenticated(AsyncSupervisionForm)}
                    />
                    <Route
                      path={['/createReview', '/manageReview/:id']}
                      component={UserIsAuthenticated(AsyncReviewForm)}
                    />
                    <Route
                      path={['/manageUsers', '/manageUsers']}
                      component={UserIsAuthenticated(AsyncManageUserForm)}
                    />
                    <Route
                      path={['/createEvent', '/manageEvent/:id']}
                      component={UserIsAuthenticated(AsyncEventForm)}
                    />
                    <Route
                      path={['/createNondirect', '/manageNondirect/:id']}
                      component={UserIsAuthenticated(AsyncNondirectForm)}
                    />

                    <Route
                      path="/wiki"
                      component={UserIsAuthenticated(WikiPage)}
                    />
                    <Route
                      path="/guides"
                      component={UserIsAuthenticated(Guides)}
                    />
                    <Route
                      path="/handbooks"
                      component={UserIsAuthenticated(Handbooks)}
                    />
                    <Route
                      path="/resources"
                      component={UserIsAuthenticated(Resources)}
                    />

                    <Route
                      path="/chat"
                      component={UserIsAuthenticated(AsyncGroupChat)}
                    />
                    <Route
                      path="/gusto/callback"
                      component={UserIsAuthenticated(AsyncGustoCallback)}
                    />
                    <Route
                      path="/admin/errorLog"
                      component={UserIsAuthenticated(AdminOnlyErrorLog)}
                    />
                    <Route
                      path="/groupTraining/:id"
                      component={UserIsAuthenticated(NonClientGroupTraining)}
                    />
                    <Route
                      path="/training"
                      component={UserIsAuthenticated(NonClientTraining)}
                    />

                    <Route path="/error" component={NotFound} />
                    <Route component={NotFound} />
                  </Switch>
                </Suspense>
              </Container>
            </div>
          )}
        />
      </div>
    </ErrorBoundary>
  );
};

export default App;
